import { useCallback, useEffect, useState } from 'react'
import { OrganizationProfile } from '@clerk/clerk-react'
import { useV2Api } from '../../../hooks/useV2Api'
import V2EmailSettings from './V2EmailSettings'
import V2TeamSettings from './V2TeamSettings'
import './V2Settings.css'

const TABS = [
  { key: 'general', label: 'General' },
  { key: 'team', label: 'Team' },
  { key: 'ai', label: 'AI Configuration' },
  { key: 'api', label: 'API Keys' },
  { key: 'email', label: 'Email' },
]

const INDUSTRIES = [
  'Construction & Building Materials',
  'Consumer Electronics',
  'Furniture & Home Goods',
  'Automotive Parts',
  'Textiles & Apparel',
  'Food & Beverage',
  'Industrial Equipment',
  'Other',
]

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'MXN']
const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Mexico_City',
  'Europe/London',
  'Asia/Shanghai',
]

const PLACEHOLDER_CONTENT = {
  ai: { icon: '🤖', text: 'AI configuration — email templates, follow-up rules, LLM settings' },
  api: { icon: '🔑', text: 'API keys — generate and manage API access tokens' },
}

const DEFAULT_FORM = {
  companyName: '',
  industry: INDUSTRIES[0],
  currency: 'USD',
  timezone: 'America/New_York',
  emailQuoteReceived: true,
  emailComparisonReady: true,
  emailFollowUpSent: false,
  emailWeeklyDigest: true,
}

function tenantToForm(tenant) {
  const s = (tenant && tenant.settings) || {}
  const n = s.notifications || {}
  return {
    companyName: tenant?.name || '',
    industry: s.industry || DEFAULT_FORM.industry,
    currency: s.currency || DEFAULT_FORM.currency,
    timezone: tenant?.timezone || DEFAULT_FORM.timezone,
    emailQuoteReceived: n.quoteReceived ?? DEFAULT_FORM.emailQuoteReceived,
    emailComparisonReady: n.comparisonReady ?? DEFAULT_FORM.emailComparisonReady,
    emailFollowUpSent: n.followUpSent ?? DEFAULT_FORM.emailFollowUpSent,
    emailWeeklyDigest: n.weeklyDigest ?? DEFAULT_FORM.emailWeeklyDigest,
  }
}

function formToTenantPayload(form, existingSettings) {
  return {
    name: form.companyName,
    timezone: form.timezone,
    settings: {
      ...(existingSettings || {}),
      industry: form.industry,
      currency: form.currency,
      notifications: {
        ...((existingSettings && existingSettings.notifications) || {}),
        quoteReceived: form.emailQuoteReceived,
        comparisonReady: form.emailComparisonReady,
        followUpSent: form.emailFollowUpSent,
        weeklyDigest: form.emailWeeklyDigest,
      },
    },
  }
}

export default function V2SettingsPage() {
  const api = useV2Api()
  const [activeTab, setActiveTab] = useState('general')
  const [form, setForm] = useState(DEFAULT_FORM)
  const [tenantSettings, setTenantSettings] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [error, setError] = useState(null)
  const [savedAt, setSavedAt] = useState(null)

  const loadTenant = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.getTenant()
      const tenant = res?.data || null
      setTenantSettings(tenant?.settings || {})
      setForm(tenantToForm(tenant))
      setDirty(false)
    } catch (err) {
      setError(err?.body?.error || err?.message || 'Failed to load settings')
    } finally {
      setLoading(false)
    }
  }, [api])

  useEffect(() => {
    loadTenant()
  }, [loadTenant])

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    setDirty(true)
    setSavedAt(null)
  }

  const toggleField = (field) => {
    setForm((prev) => ({ ...prev, [field]: !prev[field] }))
    setDirty(true)
    setSavedAt(null)
  }

  const handleSave = async () => {
    if (saving) return
    setSaving(true)
    setError(null)
    try {
      const payload = formToTenantPayload(form, tenantSettings)
      const res = await api.updateTenant(payload)
      const tenant = res?.data || null
      if (tenant) {
        setTenantSettings(tenant.settings || {})
        setForm(tenantToForm(tenant))
      }
      setDirty(false)
      setSavedAt(Date.now())
    } catch (err) {
      if (err?.status === 403) {
        setError('Only owners and admins can update workspace settings.')
      } else {
        setError(err?.body?.error || err?.message || 'Failed to save settings')
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="v2-page-header">
        <div>
          <h1 className="v2-page-title">Settings</h1>
          <p className="v2-page-subtitle">Manage your workspace preferences</p>
        </div>
      </div>

      <div className="v2-card">
        {/* Tabs */}
        <div className="v2-settings-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`v2-settings-tab${activeTab === tab.key ? ' v2-settings-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* General Tab */}
        {activeTab === 'general' && (
          <div>
            <div className="v2-settings-section">
              <h3 className="v2-settings-section-title">Company Information</h3>
              <div className="v2-settings-form-row">
                <div className="v2-form-group">
                  <label className="v2-form-label" htmlFor="settings-company-name">Company Name</label>
                  <input
                    id="settings-company-name"
                    className="v2-form-input"
                    type="text"
                    value={form.companyName}
                    onChange={(e) => updateField('companyName', e.target.value)}
                  />
                </div>
                <div className="v2-form-group">
                  <label className="v2-form-label">Industry</label>
                  <select
                    className="v2-form-input"
                    value={form.industry}
                    onChange={(e) => updateField('industry', e.target.value)}
                  >
                    {INDUSTRIES.map((ind) => (
                      <option key={ind} value={ind}>{ind}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="v2-settings-form-row">
                <div className="v2-form-group">
                  <label className="v2-form-label">Currency</label>
                  <select
                    className="v2-form-input"
                    value={form.currency}
                    onChange={(e) => updateField('currency', e.target.value)}
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="v2-form-group">
                  <label className="v2-form-label">Timezone</label>
                  <select
                    className="v2-form-input"
                    value={form.timezone}
                    onChange={(e) => updateField('timezone', e.target.value)}
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="v2-settings-section">
              <h3 className="v2-settings-section-title">Notifications</h3>
              <div className="v2-toggle-row">
                <div className="v2-toggle-info">
                  <span className="v2-toggle-label">Quote Received</span>
                  <span className="v2-toggle-desc">Get notified when a provider responds</span>
                </div>
                <label className="v2-toggle-switch">
                  <input
                    type="checkbox"
                    data-testid="toggle-emailQuoteReceived"
                    checked={form.emailQuoteReceived}
                    onChange={() => toggleField('emailQuoteReceived')}
                  />
                  <span className="v2-toggle-slider" />
                </label>
              </div>

              <div className="v2-toggle-row">
                <div className="v2-toggle-info">
                  <span className="v2-toggle-label">Comparison Ready</span>
                  <span className="v2-toggle-desc">Notify when all quotes are in and comparison is ready</span>
                </div>
                <label className="v2-toggle-switch">
                  <input
                    type="checkbox"
                    data-testid="toggle-emailComparisonReady"
                    checked={form.emailComparisonReady}
                    onChange={() => toggleField('emailComparisonReady')}
                  />
                  <span className="v2-toggle-slider" />
                </label>
              </div>

              <div className="v2-toggle-row">
                <div className="v2-toggle-info">
                  <span className="v2-toggle-label">Follow-up Sent</span>
                  <span className="v2-toggle-desc">Notify when automated follow-ups are sent</span>
                </div>
                <label className="v2-toggle-switch">
                  <input
                    type="checkbox"
                    data-testid="toggle-emailFollowUpSent"
                    checked={form.emailFollowUpSent}
                    onChange={() => toggleField('emailFollowUpSent')}
                  />
                  <span className="v2-toggle-slider" />
                </label>
              </div>

              <div className="v2-toggle-row">
                <div className="v2-toggle-info">
                  <span className="v2-toggle-label">Weekly Digest</span>
                  <span className="v2-toggle-desc">Summary of the week&apos;s activity every Monday</span>
                </div>
                <label className="v2-toggle-switch">
                  <input
                    type="checkbox"
                    data-testid="toggle-emailWeeklyDigest"
                    checked={form.emailWeeklyDigest}
                    onChange={() => toggleField('emailWeeklyDigest')}
                  />
                  <span className="v2-toggle-slider" />
                </label>
              </div>
            </div>

            <div className="v2-settings-save">
              {error && (
                <span
                  data-testid="settings-save-error"
                  style={{ color: '#c0392b', marginRight: 12, fontSize: 13 }}
                >
                  {error}
                </span>
              )}
              {savedAt && !dirty && !error && (
                <span
                  data-testid="settings-save-ok"
                  style={{ color: '#2e7d32', marginRight: 12, fontSize: 13 }}
                >
                  Saved
                </span>
              )}
              <button
                className="v2-btn v2-btn--primary"
                onClick={handleSave}
                disabled={loading || saving || !dirty}
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}

        {/* Team — real data + Clerk OrganizationProfile for invitations */}
        {activeTab === 'team' && (
          <div style={{ padding: '1.5rem' }}>
            <V2TeamSettings />
            <div style={{ marginTop: 24 }}>
              <h3 style={{ marginBottom: 10 }}>Manage members</h3>
              <OrganizationProfile
                routing="hash"
                appearance={{
                  elements: {
                    organizationMembersTableButton__action__remove: {
                      '&[aria-disabled="true"]': {
                        cursor: 'not-allowed'
                      },
                      '&[aria-disabled="true"]::after': {
                        content: '" (only admin)"',
                        marginLeft: '4px',
                        fontStyle: 'italic',
                        opacity: 0.7
                      }
                    },
                    menuButton__remove: {
                      '&[aria-disabled="true"]::after': {
                        content: '" (only admin)"',
                        marginLeft: '4px',
                        fontStyle: 'italic',
                        opacity: 0.7
                      }
                    }
                  }
                }}
              />
              <p
                data-testid="remove-member-hint"
                style={{ marginTop: 12, color: '#8F8F85', fontSize: 13 }}
              >
                An admin cannot be removed if they&apos;re the only admin in the organization.
                Invite another admin first, then retry the removal.
              </p>
            </div>
          </div>
        )}

        {/* Email — SMTP + Nylas OAuth */}
        {activeTab === 'email' && (
          <div style={{ padding: '1.5rem' }}>
            <V2EmailSettings />
          </div>
        )}

        {/* Remaining placeholder tabs (AI, API) */}
        {activeTab !== 'general' && activeTab !== 'team' && activeTab !== 'email' && PLACEHOLDER_CONTENT[activeTab] && (
          <div className="v2-settings-placeholder">
            <div className="v2-settings-placeholder-icon">
              {PLACEHOLDER_CONTENT[activeTab].icon}
            </div>
            <p>{PLACEHOLDER_CONTENT[activeTab].text}</p>
            <p style={{ fontSize: 13, marginTop: 8 }}>Coming soon in the V2 implementation.</p>
          </div>
        )}
      </div>
    </div>
  )
}
